import React, { useState, useRef } from 'react';
import { X, Upload, Download, FileSpreadsheet, Check, AlertCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ExcelUploadModal({ onClose }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xls'))) {
      setFile(droppedFile);
      setError('');
      setResult(null);
    } else {
      setError('Formato file non valido. Caricare un file Excel (.xlsx o .xls)');
    }
  };
  
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError('');
      setResult(null);
    }
  };
  
  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/employees/template`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'template_dipendenti.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setError('Errore nel download del template');
      }
    } catch (err) {
      console.error('Error downloading template:', err);
      setError('Errore nel download del template');
    }
  };
  
  const handleUpload = async () => {
    if (!file) {
      setError('Selezionare un file da caricare');
      return;
    }
    
    try {
      setUploading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/employees/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setResult(data);
      } else {
        setError(data.detail || 'Errore durante il caricamento');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Errore di connessione durante il caricamento');
    } finally {
      setUploading(false);
    }
  };
  
  const handleClose = () => {
    onClose(result?.created > 0);
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-testid="excel-upload-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-green-500 to-emerald-600">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="text-white" size={28} />
            <div>
              <h2 className="text-xl font-bold text-white">Importa Dipendenti da Excel</h2>
              <p className="text-green-100 text-sm">Carica un file Excel per aggiungere dipendenti in blocco</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white transition-colors"
            data-testid="close-modal-button"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {!result ? (
            <>
              {/* Template Download */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Download className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <h3 className="font-semibold text-blue-800 mb-1">Scarica il Template</h3>
                    <p className="text-blue-700 text-sm mb-3">
                      Utilizza il template Excel con le colonne corrette per garantire un'importazione senza errori.
                    </p>
                    <button
                      onClick={handleDownloadTemplate}
                      className="btn bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2"
                      data-testid="download-template-button"
                    >
                      <Download size={16} />
                      Scarica Template
                    </button>
                  </div>
                </div>
              </div>
              
              {/* File Drop Zone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`
                  border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200
                  ${dragOver 
                    ? 'border-green-500 bg-green-50' 
                    : file 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-300 hover:border-green-400 hover:bg-green-50/50'
                  }
                `}
                data-testid="file-drop-zone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
                
                {file ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <FileSpreadsheet className="text-green-600" size={32} />
                    </div>
                    <div>
                      <p className="font-semibold text-green-700">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Rimuovi file
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                      <Upload className="text-gray-400" size={32} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-700">
                        Trascina qui il file Excel
                      </p>
                      <p className="text-sm text-gray-500">
                        oppure clicca per selezionare
                      </p>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Formati supportati: .xlsx, .xls
                    </p>
                  </div>
                )}
              </div>
              
              {/* Error Message */}
              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
              
              {/* Instructions */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-2">Colonne supportate:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                  <div><span className="font-medium text-red-600">*</span> nome</div>
                  <div><span className="font-medium text-red-600">*</span> cognome</div>
                  <div>numero_matricola</div>
                  <div>codice_fiscale</div>
                  <div>data_nascita</div>
                  <div>luogo_nascita</div>
                  <div>sesso (M/F)</div>
                  <div>data_assunzione</div>
                  <div>ruolo</div>
                  <div>qualifica</div>
                  <div>tipo_contratto</div>
                  <div>sede</div>
                  <div>email</div>
                  <div>telefono</div>
                  <div>indirizzo</div>
                  <div>note</div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  <span className="font-medium text-red-600">*</span> Campi obbligatori
                </p>
              </div>
            </>
          ) : (
            /* Results View */
            <div className="space-y-4" data-testid="upload-results">
              {/* Summary */}
              <div className={`p-4 rounded-lg ${result.success ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                <div className="flex items-center gap-3">
                  {result.success ? (
                    <Check className="text-green-600" size={24} />
                  ) : (
                    <AlertCircle className="text-yellow-600" size={24} />
                  )}
                  <div>
                    <h3 className="font-semibold text-gray-800">{result.message}</h3>
                    <p className="text-sm text-gray-600">
                      Processate {result.total_processed} righe
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-green-600">{result.created}</p>
                  <p className="text-sm text-green-700">Dipendenti creati</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-red-600">{result.failed}</p>
                  <p className="text-sm text-red-700">Errori</p>
                </div>
              </div>
              
              {/* Created Employees */}
              {result.created_employees?.length > 0 && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-2">Dipendenti creati:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {result.created_employees.map((emp, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <Check size={14} className="text-green-600" />
                        {emp.nome} {emp.cognome}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Errors */}
              {result.errors?.length > 0 && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <h4 className="font-semibold text-red-700 mb-2">Errori riscontrati:</h4>
                  <ul className="text-sm text-red-600 space-y-1 max-h-40 overflow-y-auto">
                    {result.errors.map((err, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <AlertCircle size={14} className="text-red-600 flex-shrink-0 mt-0.5" />
                        <span>Riga {err.row}: {err.error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          {!result ? (
            <>
              <button
                onClick={handleClose}
                className="btn btn-secondary"
                data-testid="cancel-button"
              >
                Annulla
              </button>
              <button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="btn btn-primary bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="upload-button"
              >
                {uploading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Caricamento...
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    Importa Dipendenti
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              className="btn btn-primary"
              data-testid="done-button"
            >
              {result.success ? 'Chiudi' : 'Riprova'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExcelUploadModal;
